import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Gift, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POPUP_KEY = "kg_email_popup_seen";

export function SignupForm({ dark = false, prefix = "signup" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [code, setCode] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("invalid");
      const data = await res.json();
      setCode(data.discount_code);
      setStatus("success");
      localStorage.setItem(POPUP_KEY, "1");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className={`flex items-center gap-3 rounded-xl px-5 py-4 ${dark ? "bg-white/10" : "bg-honey-tint"}`} data-testid={`${prefix}-success`}>
        <CheckCircle size={20} className="text-gold flex-shrink-0" />
        <p className={`text-sm ${dark ? "text-white" : "text-brown-dark"}`}>
          You're in! Use code <strong className="text-gold font-bold">{code}</strong> for 10% off your first order.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid={`${prefix}-form`}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className={`flex-1 rounded-full px-5 py-3 text-sm outline-none border ${dark ? "bg-white/10 border-white/20 text-white placeholder-white/50 focus:border-gold" : "bg-white border-gold/20 text-brown-dark focus:border-gold"}`}
        data-testid={`${prefix}-input`}
      />
      <button type="submit" disabled={status === "loading"} className="btn-gold text-sm py-3 px-6 whitespace-nowrap disabled:opacity-60" data-testid={`${prefix}-submit`}>
        {status === "loading" ? "Sending..." : "Get 10% Off"}
      </button>
      {status === "error" && (
        <p className="text-red-400 text-xs sm:w-full" data-testid={`${prefix}-error`}>Please enter a valid email and try again.</p>
      )}
    </form>
  );
}

export function EmailCapturePopup() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.startsWith("/admin") || localStorage.getItem(POPUP_KEY)) return;
    const timer = setTimeout(() => setOpen(true), 6000);
    return () => clearTimeout(timer);
  }, [pathname]);

  if (pathname.startsWith("/admin")) return null;

  const close = () => {
    localStorage.setItem(POPUP_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm" data-testid="email-popup-overlay">
      <div className="relative bg-cream-light rounded-2xl max-w-md w-full p-8 shadow-2xl border border-gold/20" data-testid="email-popup">
        <button onClick={close} className="absolute top-4 right-4 text-gray-400 hover:text-brown-dark transition-colors" data-testid="email-popup-close" aria-label="Close">
          <X size={20} />
        </button>
        <div className="w-12 h-12 rounded-full bg-honey-tint flex items-center justify-center mb-4">
          <Gift size={22} className="text-gold" />
        </div>
        <h3 className="font-playfair text-2xl font-bold text-brown-dark mb-2">Your First Shot, 10% Off</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-6">
          Join the Ken's Gold circle and get 10% off your first order — plus wellness tips and early access to new blends.
        </p>
        <SignupForm prefix="popup" />
        <p className="text-gray-400 text-xs mt-4">No spam, ever. Unsubscribe anytime.</p>
      </div>
    </div>
  );
}
